import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { useAuth } from '../hooks/useAuth';
import { COUNTRY_CODES, getPhoneValidationRules, validatePhoneNumber } from '../constants/countries';
import Captcha from '../components/common/Captcha';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    country: '',
    agreedToTerms: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [phoneError, setPhoneError] = useState('');
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const nameInputRef = useRef(null);

  // Focus on name input when page loads
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  // Get unique countries from COUNTRY_CODES
  const uniqueCountries = Array.from(
    new Map(COUNTRY_CODES.map(item => [item.country, item])).values()
  ).sort((a, b) => a.country.localeCompare(b.country));

  const filteredCountries = uniqueCountries.filter(item =>
    item.country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
    // Validate phone in real-time
    const error = validatePhoneNumber(value, phoneCountryCode);
    setPhoneError(error || '');
  };

  const handlePhoneCountryCodeChange = (e) => {
    setPhoneCountryCode(e.target.value);
    // Re-validate with new country code
    const error = validatePhoneNumber(formData.phone, e.target.value);
    setPhoneError(error || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.agreedToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    if (!isCaptchaVerified) {
      setError('Please complete the security verification (CAPTCHA)');
      return;
    }

    setLoading(true);

    try {
      const result = await register(
        formData.email,
        formData.password,
        formData.name,
        'consumer',
        {
          phone: formData.phone || undefined,
          location: formData.location || undefined,
          country: formData.country || undefined,
        }
      );

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-62px)] bg-slate-100 px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <SEO
        title="Register at VMC Reviews"
        description="VMC Reviews – Transparent Business Listings & Verified Customer Reviews"
      />
      <div className="mx-auto flex min-h-[78vh] w-full max-w-7xl overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-300/35 ring-1 ring-slate-200 lg:min-h-[82vh]">
        <section className="relative hidden w-3/5 overflow-hidden bg-[#0F172A] p-10 text-white lg:flex lg:flex-col xl:p-14">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-24 -top-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="absolute -right-20 bottom-12 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.07),transparent_40%),radial-gradient(circle_at_85%_80%,rgba(91,79,207,0.2),transparent_45%)]" />
          </div>

          <div className="relative z-10 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-900/40">
              <span className="text-sm font-black tracking-tight">VMC</span>
            </div>
            <div className="leading-none">
              <p className="text-base font-bold tracking-tight">VMC Reviews</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">Business Platform</p>
            </div>
          </div>

          <div className="relative z-10 mt-16 max-w-lg xl:mt-20">
            <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight">Join the VMC Reviews Community</h1>
            <p className="mt-5 max-w-md text-base leading-7 text-slate-300">
              Help build a more trustworthy marketplace—one verified review at a time.
            </p>

            <div className="mt-8 pb-8 border-b border-white/10">
              <p className="text-sm font-semibold text-slate-200">Choose Your Path:</p>
              <p className="mt-2 text-sm text-slate-400">Create an account to share your experiences, save favorite businesses, and help others make confident decisions.</p>
            </div>

            <div className="mt-8">
              <p className="text-sm font-semibold uppercase tracking-wider text-slate-200">Why Join VMC Reviews?</p>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="font-medium text-indigo-300">For Your Voice</p>
                  <p className="mt-1 text-sm text-slate-400">Your reviews include verification markers that help your feedback stand out as trustworthy.</p>
                </div>
                <div>
                  <p className="font-medium text-indigo-300">For Your Search</p>
                  <p className="mt-1 text-sm text-slate-400">Access decision-ready insights like recency indicators and quality checks to find the best local services.</p>
                </div>
                <div>
                  <p className="font-medium text-indigo-300">For Everyone</p>
                  <p className="mt-1 text-sm text-slate-400">Be part of a community that values 95% review authenticity.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-10 space-y-3 hidden">
            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/90">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
              <p className="text-sm font-medium text-slate-100">Discover trusted local businesses</p>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/90">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
              <p className="text-sm font-medium text-slate-100">Read and write verified reviews</p>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/90">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
              <p className="text-sm font-medium text-slate-100">Register your own business</p>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/90">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
              <p className="text-sm font-medium text-slate-100">Trusted by 10,000+ users</p>
            </div>
          </div>

          <p className="relative z-10 mt-auto pt-10 text-sm uppercase tracking-[0.16em] text-slate-400">
            Your trusted business review platform
          </p>
        </section>

        <section className="flex w-full items-center bg-white px-5 py-10 sm:px-8 md:px-10 lg:w-2/5 lg:px-12 xl:px-14">
          <div className="mx-auto w-full max-w-md">
            <div className="mb-8 lg:hidden">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-md shadow-indigo-300/50">
                  <span className="text-sm font-black tracking-tight text-white">VMC</span>
                </div>
                <div>
                  <p className="text-[15px] font-bold tracking-tight text-slate-900">VMC Reviews</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Business Platform</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Create your account</h2>
            <p className="mt-2 text-sm text-slate-500">Join as a consumer and start sharing verified experiences.</p>

            {error && (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Full Name</label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                    placeholder="Rajesh Kumar"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] pr-12 text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                      placeholder="********"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 3l18 18M10.584 10.587a3 3 0 0 0 4.828 4.828M9 12a3 3 0 0 1 4.828-4.828" />
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Minimum 8 characters</p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] pr-12 text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                      placeholder="********"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 3l18 18M10.584 10.587a3 3 0 0 0 4.828 4.828M9 12a3 3 0 0 1 4.828-4.828" />
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Additional Information </p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">Country</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search Country..."
                        value={isCountryOpen ? countrySearch : formData.country}
                        onChange={(e) => {
                          setCountrySearch(e.target.value);
                          setIsCountryOpen(true);
                        }}
                        onFocus={() => setIsCountryOpen(true)}
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                      />
                      {isCountryOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg z-50">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((item) => (
                              <button
                                key={item.country}
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, country: item.country }));
                                  setIsCountryOpen(false);
                                  setCountrySearch('');
                                }}
                                className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                              >
                                {item.country}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-slate-500">No countries found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">City/Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]"
                      placeholder="Mumbai, Maharashtra"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Phone Number</label>
                  <div className="flex gap-2">
                      <select
                        value={phoneCountryCode}
                        onChange={handlePhoneCountryCodeChange}
                        disabled={loading}
                        className="px-3 py-[14px] rounded-lg border border-[#E2E8F0] text-[15px] text-slate-900 outline-none transition-all duration-200 focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)] bg-white"
                      >
                        {COUNTRY_CODES.map((cc, idx) => (
                          <option key={`${cc.code}-${idx}`} value={cc.code}>
                            {cc.code} {cc.countryName}
                          </option>
                        ))}
                      </select>
                      <div className="flex-1">
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          placeholder={getPhoneValidationRules(phoneCountryCode).example}
                          className={`w-full rounded-lg border px-4 py-[14px] text-[15px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 ${
                            phoneError
                              ? 'border-red-300 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.15)]'
                              : 'border-[#E2E8F0] focus:border-[#5B4FCF] focus:shadow-[0_0_0_3px_rgba(91,79,207,0.15)]'
                          }`}
                        />
                      </div>
                    </div>
                    {phoneError && (
                      <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                    )}
                    {formData.phone && !phoneError && (
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-slate-500">
                          Format: {phoneCountryCode} {formData.phone}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formData.phone.length} / {getPhoneValidationRules(phoneCountryCode).maxDigits} digits
                        </p>
                      </div>
                    )}
                  </div>
                </div>

              <Captcha onVerify={setIsCaptchaVerified} theme="purple" />

              <label className="flex items-start gap-2.5 text-sm text-slate-600">
                <input
                  type="checkbox"
                  name="agreedToTerms"
                  checked={formData.agreedToTerms}
                  onChange={handleChange}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  required
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-of-service" className="font-semibold text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="font-semibold text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                    Privacy Policy
                  </Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !isCaptchaVerified}
                className="w-full rounded-lg bg-gradient-to-r from-[#5B4FCF] to-[#7C6FE0] px-4 py-[14px] text-[15px] font-semibold text-white shadow-md shadow-indigo-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-300/50 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
              >
                {loading ? 'Creating your account...' : 'Create Consumer Account'}
              </button>
            </form>

            <div className="mt-8 text-center text-sm text-slate-600">
              <p>
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-[#5B4FCF] transition-colors hover:text-[#4A3FC2]">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RegisterPage;
